import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "../cn";

/**
 * A data table.
 *
 * The wrapper scrolls horizontally on its own so a wide table never makes the
 * page body scroll sideways — that is the bug that makes a console unusable on
 * a laptop.
 */
export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full border-collapse font-sans text-ui", className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("border-b border-rule-strong", className)} {...props} />;
}

export function TBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

export function TR({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("border-b border-rule last:border-0", className)} {...props} />;
}

export function TH({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "px-3 py-2 text-left text-micro font-semibold tracking-wide text-ink-3 uppercase",
        className,
      )}
      {...props}
    />
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligns and tabular-aligns digits so columns of numbers line up. */
  numeric?: boolean;
}

export function TD({ className, numeric, ...props }: TDProps) {
  return (
    <td
      className={cn(
        "px-3 py-2.5 align-middle text-ink",
        numeric && "text-right font-mono tabular-nums",
        className,
      )}
      {...props}
    />
  );
}

/** Shown in place of rows when there is nothing yet. */
export function TableEmpty({
  children,
  colSpan,
}: {
  children: React.ReactNode;
  colSpan: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-10 text-center text-prose text-ink-3">
        {children}
      </td>
    </tr>
  );
}
